import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Container,
} from "@mui/material";
import {
  ContentCopy as ContentCopyIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { getCountries } from "../api/Countries";
import { getConfigurations } from "../api/ConfigService";
import TextField from "./primitives/TextField";
import { useAlert } from "../hook/useAlert";
import Button from "./primitives/Button";
import AutoCompleteDropdown from "./primitives/AutoCompleteDropdown";

const ManagePage = () => {
  const { onError } = useAlert();
  const sampleData = getConfigurations();

  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value);
  };

  const handleClearFilter = () => {
    setSelectedCountry("");
  };

  const handleEditClick = (config) => {
    setSelectedConfig(config);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedConfig(null);
  };

  const handleModalInputChange = (event) => {
    const { name, value } = event.target;
    setSelectedConfig({ ...selectedConfig, [name]: value });
  };

  const filteredData = selectedCountry
    ? sampleData.filter((config) => config.country === selectedCountry)
    : sampleData;

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const countryList = await getCountries();
        setCountries(countryList.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (error) {
        onError("Error fetching countries:", error);
      }
    };

    fetchCountries();
  }, []);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            py: 2,
            px: 3,
          }}
        >
          <Typography variant="h5" component="h2">
            Manage Configurations
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Box sx={{ flexGrow: 1, mr: 4 }}>
              <AutoCompleteDropdown
                label="Country:"
                id="country"
                name="country"
                value={selectedCountry}
                onChange={handleCountryChange}
                options={[
                  { value: "", label: "Select a country" },
                  ...countries.map((country) => ({
                    value: country.name,
                    label: `${country.name} (${country.code})`,
                  })),
                ]}
              />
            </Box>
            <Button
              onClick={handleClearFilter}
              variant="contained"
              sx={{
                mt: 4,
                height: 40,
                minWidth: 80,
              }}
              name="Clear"
            />
          </Box>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f4f4f4" }}>
                  <TableCell>ID</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Campaign Name</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Form Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center" colSpan={2}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.map((config) => (
                  <TableRow key={config.id} sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}>
                    <TableCell>{config.id}</TableCell>
                    <TableCell>{config.country}</TableCell>
                    <TableCell>{config.campaignName}</TableCell>
                    <TableCell>{config.startDate}</TableCell>
                    <TableCell>{config.endDate}</TableCell>
                    <TableCell>{config.platform}</TableCell>
                    <TableCell>{config.formType}</TableCell>
                    <TableCell>{config.status}</TableCell>
                    <TableCell align="center" padding="none">
                      <IconButton size="small" color="primary">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell align="center" padding="none">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditClick(config)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {selectedConfig && (
            <Dialog open={isModalOpen} onClose={handleCloseModal} fullWidth maxWidth="md">
              <DialogTitle>
                Edit Configuration
                <IconButton
                  aria-label="close"
                  onClick={handleCloseModal}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <AutoCompleteDropdown
                      label="Country:"
                      id="country"
                      name="country"
                      value={selectedConfig.country}
                      onChange={handleModalInputChange}
                      options={[
                        { value: "", label: "Select a country" },
                        ...countries.map((country) => ({
                          value: country.name,
                          label: `${country.name} (${country.code})`,
                        })),
                      ]}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Campaign Name:"
                      id="campaignName"
                      name="campaignName"
                      value={selectedConfig.campaignName}
                      onChange={handleModalInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="date"
                      label="Start Date:"
                      id="startDate"
                      name="startDate"
                      value={selectedConfig.startDate}
                      onChange={handleModalInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="date"
                      label="End Date:"
                      id="endDate"
                      name="endDate"
                      value={selectedConfig.endDate}
                      onChange={handleModalInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Platform:"
                      id="platform"
                      name="platform"
                      value={selectedConfig.platform}
                      onChange={handleModalInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Form Type:"
                      id="formType"
                      name="formType"
                      value={selectedConfig.formType}
                      onChange={handleModalInputChange}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button variant="contained" onClick={handleCloseModal} name="Save Changes" />
                <Button variant="outlined" onClick={handleCloseModal} name="Cancel" color="error" />
              </DialogActions>
            </Dialog>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default ManagePage;
